import { collection, addDoc, Timestamp } from "firebase/firestore";
import { db } from "../config/firebase";

/**
 * Notification Types
 */
export type NotificationType =
  | "claim_approved"
  | "claim_rejected"
  | "bar_verified"
  | "subscription_expiring"
  | "subscription_expired"
  | "trial_expiring"
  | "trial_expired"
  | "content_approved"
  | "content_rejected";

/**
 * Notification Interface
 */
export interface Notification {
  type: NotificationType;
  title: string;
  message: string;
  claimId?: string;
  barId?: string;
  createdAt: Timestamp;
  read: boolean;
  actionUrl?: string;
}

/**
 * Sendet eine "Claim Approved" Notification an einen User
 */
export const sendClaimApprovedNotification = async (
  userId: string,
  barName: string,
  claimId: string,
  barId: string
): Promise<void> => {
  try {
    const notification: Omit<Notification, "createdAt"> = {
      type: "claim_approved",
      title: "Bar-Claim genehmigt! 🎉",
      message: `Dein Antrag für "${barName}" wurde genehmigt! Du hast jetzt 3 Monate FREE Trial und kannst deine Bar verwalten.`,
      claimId,
      barId,
      read: false,
      actionUrl: `/owner/bars/${barId}`, // Owner Dashboard URL
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending claim approved notification:", error);
    throw error;
  }
};

/**
 * Sendet eine "Claim Rejected" Notification an einen User
 */
export const sendClaimRejectedNotification = async (
  userId: string,
  barName: string,
  claimId: string,
  barId: string,
  rejectionReason: string
): Promise<void> => {
  try {
    const notification: Omit<Notification, "createdAt"> = {
      type: "claim_rejected",
      title: "Bar-Claim abgelehnt",
      message: `Dein Antrag für "${barName}" wurde leider abgelehnt.\n\nGrund: ${rejectionReason}\n\nDu kannst einen neuen Antrag mit korrigierten Informationen stellen.`,
      claimId,
      barId,
      read: false,
      actionUrl: `/bars/${barId}`, // User kann Bar nochmal ansehen
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending claim rejected notification:", error);
    throw error;
  }
};

/**
 * Sendet eine "Bar Verified" Notification an den Bar Owner
 */
export const sendBarVerifiedNotification = async (
  userId: string,
  barName: string,
  barId: string
): Promise<void> => {
  try {
    const notification: Omit<Notification, "createdAt"> = {
      type: "bar_verified",
      title: "Bar verifiziert! ✓",
      message: `Deine Bar "${barName}" wurde von unserem Admin-Team verifiziert und wird jetzt prominent in der App angezeigt.`,
      barId,
      read: false,
      actionUrl: `/owner/bars/${barId}`,
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending bar verified notification:", error);
    throw error;
  }
};

/**
 * Sendet eine "Trial Expiring" Notification (7 Tage vor Ablauf)
 */
export const sendTrialExpiringNotification = async (
  userId: string,
  barName: string,
  barId: string,
  daysRemaining: number
): Promise<void> => {
  try {
    const notification: Omit<Notification, "createdAt"> = {
      type: "trial_expiring",
      title: "Trial läuft bald ab ⏰",
      message: `Deine kostenlose Trial-Periode für "${barName}" endet in ${daysRemaining} Tagen. Aktiviere jetzt ein Subscription-Paket, um weiterhin alle Features nutzen zu können.`,
      barId,
      read: false,
      actionUrl: `/owner/subscription`,
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending trial expiring notification:", error);
    throw error;
  }
};

/**
 * Sendet eine "Content Approved" Notification (z.B. Drink, Event, Offer)
 */
export const sendContentApprovedNotification = async (
  userId: string,
  contentType: "drink" | "event" | "offer",
  contentName: string,
  barName: string,
  barId: string
): Promise<void> => {
  try {
    const typeLabels = {
      drink: "Getränk",
      event: "Event",
      offer: "Angebot",
    };

    const notification: Omit<Notification, "createdAt"> = {
      type: "content_approved",
      title: `${typeLabels[contentType]} genehmigt! ✓`,
      message: `Dein ${typeLabels[contentType]} "${contentName}" für "${barName}" wurde genehmigt und ist jetzt live.`,
      barId,
      read: false,
      actionUrl: `/owner/bars/${barId}`,
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending content approved notification:", error);
    throw error;
  }
};

/**
 * Sendet eine "Content Rejected" Notification
 */
export const sendContentRejectedNotification = async (
  userId: string,
  contentType: "drink" | "event" | "offer",
  contentName: string,
  barName: string,
  barId: string,
  rejectionReason: string
): Promise<void> => {
  try {
    const typeLabels = {
      drink: "Getränk",
      event: "Event",
      offer: "Angebot",
    };

    const notification: Omit<Notification, "createdAt"> = {
      type: "content_rejected",
      title: `${typeLabels[contentType]} abgelehnt`,
      message: `Dein ${typeLabels[contentType]} "${contentName}" für "${barName}" wurde abgelehnt.\n\nGrund: ${rejectionReason}`,
      barId,
      read: false,
      actionUrl: `/owner/bars/${barId}`,
    };

    await addDoc(collection(db, "users", userId, "notifications"), {
      ...notification,
      createdAt: Timestamp.now(),
    });
  } catch (error) {
    console.error("Error sending content rejected notification:", error);
    throw error;
  }
};
